import React, { useEffect, useMemo, useState } from 'react'
import { BoxProps } from 'rebass'

import { MeQuery, useCardBalanceLazyQuery, useGetTransactionDetailsPortalLazyQuery } from '../../types/graphql'

import CreditCardOutlineSvg from '@svgs/payment_method.svg'
import { useTranslation } from '../../i18n'
import { useSTheme } from 'styles/theme'
import { LocalStorageKeys } from 'utils/localStorage'
import moment from 'moment'
import { useAuthenticatedUser } from '../../v2/context/user'
import XCard, { XCARD_TYPE } from 'v2/containers/xCard'
import XIcon from 'v2/components/XIcon'
import XText from 'v2/components/XText'
import { BUTTON_STYLE_TYPE, XTEXT_SIZE } from 'v2/styles/constants'
import XButton from 'v2/components/XButton'
import XLink from 'v2/components/XLink'
import styles from './style.module.scss'
import ConditionalRender from 'utils/conditionalRender'
import { formatMoney } from 'utils/money'
import FPopUp from 'components/BaseComponents/FPopUp'
import SLoadingIndicator from 'components/opinionless/SLoadingIndicator'
import XColors from 'v2/styles/colors'

type Props = BoxProps

const HomePaymentMethodV2: React.FC<Props> = (props: Props) => {
    const { sx = {}, ...otherProps } = props
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()
    const [popupOpen, setPopUpOpen] = useState(false)
    const [transactions, setTransactions] = useState([])

    const [
        fetchTransactionDetails,
        { data: latestTransactionData, loading: latestTransactionLoading },
    ] = useGetTransactionDetailsPortalLazyQuery()

    useEffect(() => {
        if (popupOpen) {
            fetchTransactionDetails({})
        }
    }, [popupOpen, fetchTransactionDetails])

    useEffect(() => {
        if (latestTransactionData?.transactionDetailsPortal?.length)
            setTransactions(latestTransactionData?.transactionDetailsPortal as any)
    }, [latestTransactionData])

    const [callCardBalance, { data, called, loading }] = useCardBalanceLazyQuery()
    const { authenticatedUser, transferMethods } = useAuthenticatedUser()
    const localStorageBalance = useMemo(() => {
        return JSON.parse(localStorage.getItem(LocalStorageKeys.CARD_BALANCE))
    }, [])
    const [balance, setBalance] = useState(localStorageBalance?.value || 0)

    const transferMethod = useMemo(() => {
        if (!authenticatedUser || !authenticatedUser.paymentProfile.transferMethods.edges.length) {
            return null
        } else {
            const edge = authenticatedUser.paymentProfile?.transferMethods.edges.find(
                (transferMethodEdge: MeQuery['me']['paymentProfile']['transferMethods']['edges'][0]) =>
                    transferMethodEdge.node.isCurrentMethod
            )
            if (edge) {
                return edge.node
            } else {
                return null
            }
        }
    }, [authenticatedUser, transferMethods])

    const transferMethodText = useMemo(() => {
        if (!transferMethod) {
            return null
        } else {
            return transferMethod.oneLiner
        }
    }, [transferMethod, t])

    const shouldBeDisplayed = useMemo(() => {
        return transferMethod?.type === 'PREPAID_CARD' && transferMethod?.isCurrentMethod
    }, [transferMethod])

    const shouldCallBalanceApi = useMemo(() => {
        return (
            ((shouldBeDisplayed && !called && !loading && !localStorageBalance) ||
                moment(localStorageBalance?.expirationDate) < moment()) &&
            transferMethod
        )
    }, [localStorageBalance, shouldBeDisplayed])

    useEffect(() => {
        if (shouldCallBalanceApi) {
            void callCardBalance({ variables: { id: transferMethod.id } })
        }
    }, [localStorageBalance])

    useEffect(() => {
        if (data?.cardBalance !== undefined && shouldBeDisplayed) {
            localStorage.setItem(
                'balance',
                JSON.stringify({ value: data?.cardBalance, expirationDate: moment().add(20, 'minutes') })
            )
            setBalance(data?.cardBalance)
        }
    }, [data])

    return (
        <div className={styles.payment__wrapper}>
            <XCard type={XCARD_TYPE.HOME}>
                <div className={styles.payment__title}>
                    <XIcon icon={CreditCardOutlineSvg} tintColor="black" size={20} />
                    <div className={styles.payment__transfer_last}>
                        <XText color={'black'} size={XTEXT_SIZE.LARGE_SEMI_BOLDED}>
                            {t('home_payment_method')}
                        </XText>
                    </div>
                </div>
                <div
                    className={
                        shouldBeDisplayed
                            ? styles.payment__transfer_method__wrapper
                            : styles.payment__transfer_method__wrapper_smaller
                    }
                >
                    <div className={styles.payment__transfer_method}>
                        <XText size={XTEXT_SIZE.LARGE_UNBOLDED} color={'black'}>
                            {transferMethodText}
                        </XText>
                    </div>
                    <ConditionalRender if={shouldBeDisplayed}>
                        <div className={styles.payment__balance}>{`${formatMoney(balance)}`}</div>
                    </ConditionalRender>
                    <ConditionalRender if={transferMethod?.type === 'PREPAID_CARD'}>
                        <div className={styles.transactions__text}>
                            <XText
                                color={XColors.primaryColor}
                                size={XTEXT_SIZE.MEDIUM_BIGGER}
                                onClick={() => setPopUpOpen(true)}
                            >
                                {t('recent_transactions')}
                            </XText>
                        </div>
                    </ConditionalRender>
                </div>
                <FPopUp isOpen={popupOpen} onClickOutside={() => setPopUpOpen(false)}>
                    {latestTransactionLoading ? (
                        <div className={styles.transactions__loader}>
                            <SLoadingIndicator size={22} color={'black'} />
                        </div>
                    ) : (
                        <div className={styles.transactions__wrapper}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 0',
                                }}
                            >
                                <XText color={'black'} size={XTEXT_SIZE.LARGE_SEMI_BOLDED}>
                                    {t('recent_transactions')}
                                </XText>
                                <span className={styles.transactions__escape} onClick={() => setPopUpOpen(false)}>
                                    x
                                </span>
                            </div>
                            <ul className={styles.transactions__list}>
                                {transactions?.length === 0 && <span>No transactions recorded yet.</span>}
                                {transactions?.map((transaction, index) => (
                                    <li key={index} className={styles.transaction__item}>
                                        <div style={{ maxWidth: '200px' }}>
                                            <XText color={'#5A5A5A'}>{transaction.merchantDescription}</XText>
                                            <XText color={'#999999'}>
                                                {moment(transaction.date).format('MMMM D, YYYY')}
                                            </XText>
                                            <XText color={'#999999'}>{transaction.mcc}</XText>
                                        </div>
                                        <div>
                                            <XText color={'#5A5A5A'}>
                                                {transaction.amount < 0
                                                    ? `-$${Math.abs(transaction.amount).toFixed(2)}`
                                                    : `$${transaction.amount.toFixed(2)}`}
                                            </XText>{' '}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </FPopUp>
                <div className={styles.payment__button}>
                    <XLink
                        href={`${
                            window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                        }/account`}
                        target={'_self'}
                    >
                        <XButton
                            onClick={() => {}}
                            styleType={BUTTON_STYLE_TYPE.PRIMARY}
                            ga4_key={t('transfer_method_edit_button', { lang: 'en' })}
                        >
                            {t('transfer_method_edit_button')}
                        </XButton>
                    </XLink>
                </div>
            </XCard>
        </div>
    )
}

export default HomePaymentMethodV2
